import { 
  Play, 
  FileText, 
  FileEdit, 
  Terminal, 
  AlertTriangle, 
  CheckCircle2,
  Search,
  Compass,
  FileSearch,
  RefreshCw,
  Zap,
  LucideIcon
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { AgentEvent, AgentEventType } from '@/lib/types'
import { formatDistanceToNow } from 'date-fns'

export interface EventDetailCardProps {
  /** The agent event to display */
  event: AgentEvent
  /** Visual variant */
  variant?: 'default' | 'compact'
  /** Additional CSS classes */
  className?: string
}

type EventConfig = {
  icon: LucideIcon
  label: string
  iconColorClass: string
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline'
}

const eventTypeConfig: Record<AgentEventType, EventConfig> = {
  agent_started: {
    icon: Play,
    label: 'Agent Started',
    iconColorClass: 'text-blue-500',
    badgeVariant: 'default',
  },
  file_read: {
    icon: FileText,
    label: 'File Read',
    iconColorClass: 'text-gray-500',
    badgeVariant: 'secondary',
  },
  file_modified: {
    icon: FileEdit,
    label: 'File Modified',
    iconColorClass: 'text-yellow-500',
    badgeVariant: 'outline',
  },
  command_executed: {
    icon: Terminal,
    label: 'Command Executed',
    iconColorClass: 'text-purple-500',
    badgeVariant: 'secondary',
  },
  error: {
    icon: AlertTriangle,
    label: 'Error',
    iconColorClass: 'text-red-500',
    badgeVariant: 'destructive',
  },
  run_completed: {
    icon: CheckCircle2,
    label: 'Run Completed',
    iconColorClass: 'text-green-500',
    badgeVariant: 'default',
  },
  iteration_completed: {
    icon: Zap,
    label: 'Iteration',
    iconColorClass: 'text-orange-500',
    badgeVariant: 'outline',
  },
  discovery_started: {
    icon: Search,
    label: 'Discovery Started',
    iconColorClass: 'text-cyan-500',
    badgeVariant: 'secondary',
  },
  scout_iteration: {
    icon: Compass,
    label: 'Scout Iteration',
    iconColorClass: 'text-teal-500',
    badgeVariant: 'secondary',
  },
  evidence_found: {
    icon: FileSearch,
    label: 'Evidence Found',
    iconColorClass: 'text-emerald-500',
    badgeVariant: 'secondary',
  },
  discovery_complete: {
    icon: CheckCircle2,
    label: 'Discovery Complete',
    iconColorClass: 'text-green-500',
    badgeVariant: 'default',
  },
  discovery_extended: {
    icon: RefreshCw,
    label: 'Discovery Extended',
    iconColorClass: 'text-amber-500',
    badgeVariant: 'outline',
  },
}

function formatTimestamp(timestamp: string): { relative: string; absolute: string } {
  const date = new Date(timestamp)
  return {
    relative: formatDistanceToNow(date, { addSuffix: true }),
    absolute: date.toLocaleString(),
  }
}

function MetadataDisplay({ event }: { event: AgentEvent }) {
  const { event_type, metadata } = event

  if (!metadata || Object.keys(metadata).length === 0) {
    return null
  }

  switch (event_type) {
    case 'file_read':
    case 'file_modified':
      return metadata.file_path ? (
        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
          {String(metadata.file_path)}
        </code>
      ) : null

    case 'command_executed':
      return metadata.command ? (
        <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
          {String(metadata.command)}
        </code>
      ) : null

    case 'error':
      return metadata.message ? (
        <span className="text-xs text-red-600 dark:text-red-400">
          {String(metadata.message)}
        </span>
      ) : null

    case 'iteration_completed': {
      const inputTokens = metadata.input_tokens as number | undefined
      const outputTokens = metadata.output_tokens as number | undefined
      const totalInput = metadata.total_input_tokens as number | undefined
      const totalOutput = metadata.total_output_tokens as number | undefined
      const toolsCalled = metadata.tools_called as string[] | undefined
      
      return (
        <div className="space-y-1">
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
              +{inputTokens?.toLocaleString() ?? 0} in
            </span>
            <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
              +{outputTokens?.toLocaleString() ?? 0} out
            </span>
            <span className="text-muted-foreground">
              (total: {totalInput?.toLocaleString() ?? 0} / {totalOutput?.toLocaleString() ?? 0})
            </span>
          </div>
          {toolsCalled && toolsCalled.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {toolsCalled.map((tool, i) => (
                <code key={i} className="text-xs bg-muted px-1 py-0.5 rounded font-mono">
                  {tool}
                </code>
              ))}
            </div>
          )}
        </div>
      )
    }

    default:
      return null
  }
}

/**
 * Displays details of an agent execution event.
 * Shows event type icon, badge, timestamp, and relevant metadata.
 * 
 * @example
 * ```tsx
 * <EventDetailCard
 *   event={{
 *     event_type: 'file_modified',
 *     timestamp: '2025-01-15T10:00:00Z',
 *     metadata: { file_path: '/src/App.tsx' }
 *   }}
 * />
 * ```
 */
export function EventDetailCard({
  event,
  variant = 'default',
  className,
}: EventDetailCardProps) {
  const config = eventTypeConfig[event.event_type]
  const Icon = config.icon
  const time = formatTimestamp(event.timestamp)

  return (
    <article
      className={cn(
        'flex items-start gap-3 rounded-lg border bg-card',
        variant === 'default' ? 'p-3' : 'p-2',
        className
      )}
    >
      <div
        data-testid="event-icon"
        className={cn(
          'flex-shrink-0 mt-0.5',
          config.iconColorClass
        )}
      >
        <Icon className={variant === 'default' ? 'h-5 w-5' : 'h-4 w-4'} />
      </div>

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <Badge variant={config.badgeVariant}>
            {config.label}
          </Badge>
          <span
            data-testid="event-timestamp"
            className="text-xs text-muted-foreground"
            title={time.absolute}
          >
            {time.relative}
          </span>
        </div>

        <MetadataDisplay event={event} />
      </div>
    </article>
  )
}
